import {
  ALIGN_CENTER,
  BG_CELL_TITLE,
  BORDER_CELL,
  FONT_TITLE,
} from '../data/excelJsData';
import { getAlphabet } from '../utils/excelJsUtil';

const ALTURA_BASE_FILA = 18;

/**
 * Decorador para crear tablas con soporte de subfilas físicas en ExcelJs
 *
 * Permite crear tablas donde un valor puede expandirse en múltiples filas físicas
 * mediante separadores \n, y saltos de línea visuales mediante separadores <br>
 *
 * Funciones públicas:
 * - addTableConSubfilas: Crea tabla con expansión de subfilas
 */
class TableSubFilasExcelJsDecorator {
  constructor(worksheet, props = {}) {
    this.ws = worksheet;
    this.separadorSubfilas = props.separadorSubfilas ?? '\n';
    this.separadorVisual = props.separadorVisual ?? '<br>';
  }

  /**
   * Crea tabla con soporte para subfilas físicas y saltos visuales
   * @param {number} startRow - Fila inicial donde se crearán los headers
   * @param {array} columns - Columnas con { header, key, width, alignment }
   * @param {array} data - Datos con valores que pueden contener separadores
   * @param {object} props - Configuración adicional
   * @param {boolean} props.mergeVertical - Combinar celdas con valor único (default: true)
   * @param {boolean} props.calcularAlturaDinamica - Calcular altura según contenido (default: true)
   * @returns {object} Devuelve el this para encadenar funciones
   */
  addTableConSubfilas(startRow, columns, data, props = {}) {
    const {
      mergeVertical = true,
      calcularAlturaDinamica = true,
      showHeaders = true,
    } = props;

    this._setDataTable(startRow, columns, data, showHeaders);
    if (showHeaders) this._addHeaders();
    this._procesarDatosConSubfilas({
      mergeVertical,
      calcularAlturaDinamica,
    });

    return this;
  }

  /**
   * Carga las propiedades necesarias para crear la tabla
   * @private
   */
  _setDataTable(startRow, columns, data, showHeaders = true) {
    this.ALPHABET = getAlphabet();
    this.startRow = startRow;
    this.columns = columns;
    this.data = data;
    this.currentRow = showHeaders ? startRow + 1 : startRow;
  }

  /**
   * Crea los headers de la tabla
   * @private
   */
  _addHeaders() {
    this.ws.columns = this.columns;
    const headerRow = this.ws.getRow(this.startRow);
    headerRow.height = 35;

    this.columns.forEach((columna, index) => {
      const cell = headerRow.getCell(index + 1);
      cell.value = columna.header;
      cell.font = { ...this.FONT_TITLE };
      cell.alignment = this.ALIGN_CENTER;
      cell.border = this.BORDER_CELL;
      cell.fill = this.BG_CELL_TITLE;
    });
  }

  _procesarDatosConSubfilas(props) {
    const { mergeVertical, calcularAlturaDinamica } = props;

    this.data.forEach((registro, registroIndex) => {
      const valoresPorCampo = this.columns.map(columna =>
        this._dividirValorEnSubfilas(registro[columna.key] || '')
      );

      const numeroFilas = Math.max(
        ...valoresPorCampo.map(valor => valor.length)
      );
      const filaInicio = this.currentRow;

      this._procesarFilasRegistro(
        valoresPorCampo,
        numeroFilas,
        registroIndex,
        calcularAlturaDinamica
      );

      if (mergeVertical && numeroFilas > 1) {
        this._aplicarMergeVertical(
          valoresPorCampo,
          filaInicio,
          filaInicio + numeroFilas - 1
        );
      }

      this.currentRow += numeroFilas;
    });
  }

  /**
   * Procesa y da formato a varias filas de un registro en una hoja de Excel.
   *
   * @param {Object} valoresPorCampo - Objeto que contiene los valores de cada campo para la fila.
   * @param {number} numeroFilas - Número de filas a procesar para el registro.
   * @param {number} registroIndex - Índice del registro actual, utilizado para alternar estilos de fila.
   * @param {boolean} calcularAlturaDinamica - Indica si se debe calcular la altura de la fila dinámicamente según el contenido.
   */
  _procesarFilasRegistro(
    valoresPorCampo,
    numeroFilas,
    registroIndex,
    calcularAlturaDinamica
  ) {
    const esFilaAlterna = registroIndex % 2 !== 0;

    for (let indice = 0; indice < numeroFilas; indice++) {
      const fila = this.ws.getRow(this.currentRow + indice);
      const lineasMaxima = this._procesarColumnasFila(
        fila,
        valoresPorCampo,
        indice,
        esFilaAlterna
      );

      if (calcularAlturaDinamica) {
        fila.height = lineasMaxima * ALTURA_BASE_FILA;
      }
    }
  }

  /**
   * Procesa las columnas de una fila aplicando estilos y asignando valores.
   * @param {Object} fila - Objeto de la fila a procesar.
   * @param {Array} valoresPorCampo - Valores correspondientes a cada columna.
   * @param {number} indiceFila - Índice de la fila actual.
   * @param {boolean} esFilaAlterna - Indica si la fila es alterna para aplicar estilos.
   * @returns {number} Máximo número de líneas entre las columnas procesadas.
   */
  _procesarColumnasFila(fila, valoresPorCampo, indiceFila, esFilaAlterna) {
    const lineasPorColumna = this.columns.map((columna, colIndex) => {
      const cell = fila.getCell(colIndex + 1);
      const valorCelda = this._obtenerValorCelda(
        valoresPorCampo[colIndex],
        indiceFila
      );
      this._aplicarEstilosCelda(cell, columna.alignment, esFilaAlterna);
      return this._asignarValorCelda(cell, valorCelda, columna.width);
    });

    return Math.max(...lineasPorColumna);
  }

  /**
   * Asigna un valor a una celda y calcula el número de líneas que ocupa el contenido.
   *
   * @param {Object} cell - La celda del documento Excel a la cual se asignará el valor.
   * @param {string} valorCelda - El valor a asignar en la celda. Puede contener separadores visuales.
   * @param {number} anchoColumna - El ancho de la columna para estimar el número de líneas de texto.
   *
   * @returns {number} El número de líneas de texto que ocupa el valor en la celda.
   *                   Retorna 1 si el valor está vacío.
   *
   * @description
   * Si el valor contiene separadores visuales, crea un texto enriquecido con saltos visuales
   * y calcula el total de líneas sumando las líneas de cada segmento.
   * En caso contrario, asigna el valor directamente y calcula las líneas del texto completo.
   */
  _asignarValorCelda(cell, valorCelda, anchoColumna) {
    if (!valorCelda) return 1;
    if (valorCelda.includes(this.separadorVisual)) {
      cell.value = this._crearRichTextConSaltosVisuales(valorCelda);
      return valorCelda
        .split(this.separadorVisual)
        .reduce(
          (total, segmento) =>
            total + this._estimarLineasTexto(segmento, anchoColumna),
          0
        );
    }
    cell.value = valorCelda;
    return this._estimarLineasTexto(valorCelda, anchoColumna);
  }

  _estimarLineasTexto(texto, anchoColumna) {
    if (!texto) return 1;
    const charsPerLine = anchoColumna || 30;
    return Math.max(1, Math.ceil(texto.length / charsPerLine));
  }

  /**
   * Divide un valor en subfilas físicas usando el separador configurado
   * @private
   */
  _dividirValorEnSubfilas(valor) {
    if (!valor) return [''];
    return valor.includes(this.separadorSubfilas)
      ? valor.split(this.separadorSubfilas).filter(subvalor => subvalor.trim())
      : [valor];
  }

  /**
   * Obtiene el valor correcto para una celda según el índice de subfila
   * @private
   */
  _obtenerValorCelda(valores, indiceSubfila) {
    if (valores.length === 1 && indiceSubfila === 0) {
      return valores[0];
    }

    if (valores.length > 1 && indiceSubfila < valores.length) {
      return valores[indiceSubfila];
    }

    return '';
  }

  /**
   * Crea un objeto richText para manejar saltos visuales dentro de una celda
   * @private
   */
  _crearRichTextConSaltosVisuales(valor) {
    const lineasVisuales = valor.split(this.separadorVisual);
    return {
      richText: lineasVisuales.map((linea, idx) => ({
        text: idx === 0 ? linea : `\n${linea}`,
      })),
    };
  }

  /**
   * Aplica estilos a una celda (alineación, bordes, relleno)
   * @private
   */
  _aplicarEstilosCelda(cell, alignment) {
    cell.alignment = {
      horizontal: alignment?.horizontal || 'center',
      vertical: alignment?.vertical || 'middle',
      wrapText: true,
    };
    cell.border = this.BORDER_CELL;
  }

  /**
   * Combina celdas verticalmente para campos con un solo valor
   * @private
   */
  _aplicarMergeVertical(valoresPorCampo, filaInicio, filaFin) {
    this.columns.forEach((_, indiceColumna) => {
      const valores = valoresPorCampo[indiceColumna];
      if (valores.length === 1 && filaFin > filaInicio) {
        const letraColumna = this.ALPHABET[indiceColumna];
        this.ws.mergeCells(
          `${letraColumna}${filaInicio}:${letraColumna}${filaFin}`
        );
      }
    });
  }
}

/**
 * Agrega propiedades de estilos a la clase para reutilizar código
 */
TableSubFilasExcelJsDecorator.prototype.ALIGN_CENTER = ALIGN_CENTER;
TableSubFilasExcelJsDecorator.prototype.BG_CELL_TITLE = BG_CELL_TITLE;
TableSubFilasExcelJsDecorator.prototype.BORDER_CELL = BORDER_CELL;
TableSubFilasExcelJsDecorator.prototype.FONT_TITLE = FONT_TITLE;

export default TableSubFilasExcelJsDecorator;
